import { useSelectFilterStore } from '@app/stores/tables/SelectFilterStore'

interface SyncInitialQueryParamsOptions {
  queryParams: Record<string, any>
  fetchContent: () => void
}

/**
 * Syncs initial URL query parameters with the select filter store.
 * If query parameters are present and filters haven't been synced yet,
 * updates the store and re-fetches content
 *
 * @param {SyncInitialQueryParamsOptions} options - Options for syncing query params.
 */
export function syncInitialQueryParamsWithSelectFilters({
  queryParams,
  fetchContent,
}: SyncInitialQueryParamsOptions): void {
  const selectFiltersStore = useSelectFilterStore()
  const filtersObject = selectFiltersStore.asObject

  let canFetchContent = false

  if (hasMissingClassifiers(queryParams, filtersObject)) {
    getClassifierIds(queryParams).forEach((classifierId: string) => {
      const classifierValueId = queryParams[classifierId]

      selectFiltersStore.setSelectedOption(classifierId, classifierValueId)
    })

    canFetchContent = true
  }

  if (canFetchContent) {
    //
    // fetch content with the initial URL query params
    //
    fetchContent()
  }
}

/**
 * Checks if the query has any classifiers but filters not
 *
 * @param {Record<string, any>} queryParams - URL query parameters
 * @param {Record<string, any>} filtersObject - the select filter store as object
 * @returns {boolean} True if at least one classifier parameter is present
 */
function hasMissingClassifiers(
  queryParams: Record<string, any>,
  filtersObject: Record<string, any>,
): boolean {
  return hasClassifierParams(queryParams) && !filtersObject?.classifier_value_ids
}

/**
 * Retrieves keys from queryParams that include 'classifier-'.
 *
 * @param {Record<string, any>} queryParams - URL query parameters
 * @returns {string[]} Classifier keys
 */
function getClassifierIds(queryParams: Record<string, any>): string[] {
  return Object.keys(queryParams).filter((key) => key.includes('classifier-'))
}

/**
 * Determines if any classifier parameters exist in queryParams.
 *
 * @param {Record<string, any>} queryParams - URL query parameters
 * @returns {boolean} True if at least one classifier parameter is present
 */
function hasClassifierParams(queryParams: Record<string, any>): boolean {
  return getClassifierIds(queryParams).length > 0
}
